import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { typography } from '../../../styles/typography.js';
import Card from '../../ui/card.js';

const ItemsWithHighestMaintenance = ({
  data = [],
  isLoading = false,
  error = null,
  onRetry,
}) => {
  const highestMaintenanceMaxValue = useMemo(() => {
    if (!data.length) return 1;
    return data.reduce(
      (max, item) => Math.max(max, Number(item.value) || 0),
      1
    );
  }, [data]);

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex flex-col items-center justify-center py-8 gap-3">
          <div className="h-10 w-10 border-4 border-t-transparent border-blue-500 rounded-full animate-spin" />
          <p className={typography.p} style={{ color: '#333333' }}>
            Loading maintenance insights...
          </p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex flex-col items-center justify-center gap-3 py-8 text-center">
          <p className={typography.p} style={{ color: '#DC2626' }}>
            {error}
          </p>
          {onRetry && (
            <button
              type="button"
              onClick={onRetry}
              className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm hover:bg-blue-700 transition-colors"
            >
              Retry
            </button>
          )}
        </div>
      );
    }

    if (!data.length) {
      return (
        <div className="flex flex-col items-center justify-center gap-3 py-8 text-center">
          <svg
            className="w-12 h-12 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
            />
          </svg>
          <p className={typography.p} style={{ color: '#666666' }}>
            No maintenance insights available for this period.
          </p>
          <p
            className={typography.p}
            style={{ color: '#999999', fontSize: '0.875rem' }}
          >
            Adjust the filter to see relevant items.
          </p>
        </div>
      );
    }

    return (
      <>
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-full bg-white/80 flex items-center justify-center shadow-inner">
            <svg
              className="w-5 h-5 text-[#0A3D62]"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 100 20 10 10 0 000-20z"
              />
            </svg>
          </div>
          <p className={`${typography.p} text-black/80`}>
            Helps identify unreliable equipment that may need replacement.
          </p>
        </div>

        <div className="space-y-4 flex-1">
          {data.map(item => (
            <div key={item.label} className="flex flex-col gap-2 text-black">
              <div className="flex items-center justify-between">
                <p className={`${typography.p} font-semibold`}>{item.label}</p>
                <span className={`${typography.p} text-black/80`}>
                  {item.value}
                </span>
              </div>
              <div className="h-3 rounded-full bg-white/70 relative overflow-hidden">
                <div
                  className="absolute inset-y-0 left-0 rounded-full"
                  style={{
                    width: `${(item.value / highestMaintenanceMaxValue) * 100}%`,
                    backgroundColor: item.color,
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </>
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <h2
        className={typography.h3}
        style={{ color: '#000000', marginBottom: '0.5rem' }}
      >
        Items with Highest Maintenance
      </h2>
      <Card.Gradient className="p-6 space-y-5 flex-1 flex flex-col">
        {renderContent()}
      </Card.Gradient>
    </div>
  );
};

ItemsWithHighestMaintenance.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
      color: PropTypes.string,
    })
  ),
  isLoading: PropTypes.bool,
  error: PropTypes.string,
  onRetry: PropTypes.func,
};

export default ItemsWithHighestMaintenance;
